/**
 * AuthModal - Multi-Provider Authentication
 *
 * A modal overlay that allows users to sign in without leaving the HomeScreen.
 * Supports:
 * - Google Sign-In (one-click)
 * - Magic Link (passwordless email)
 *
 * Matches the SuperJarvis cyan theme.
 */
import { useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { GlassCard, AtomIcon, X } from './shared';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Google Logo SVG Component
function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

export function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const { 
    step, 
    sentToEmail, 
    loading, 
    error, 
    sendLink, 
    completeSignIn, 
    signInWithGoogle,
    resetFlow,
    clearError 
  } = useAuthContext();
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);

  if (!isOpen) return null;

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      await sendLink(email.trim());
    }
  };

  const handleCompleteSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (confirmEmail.trim()) {
      await completeSignIn(confirmEmail.trim());
    }
  };

  const handleGoogleSignIn = async () => {
    clearError();
    await signInWithGoogle();
  };

  const handleClose = () => {
    resetFlow();
    setEmail('');
    setConfirmEmail('');
    setShowEmailForm(false);
    onClose();
  };

  const handleBackToOptions = () => {
    resetFlow();
    setEmail('');
    setShowEmailForm(false);
    clearError();
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md animate-in fade-in zoom-in-95 duration-300">
        <GlassCard className="p-8 relative">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-cyan-400/10 text-cyan-400/60 hover:text-cyan-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="flex items-center space-x-3 mb-6">
            <AtomIcon className="w-10 h-10 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-spin-slow" />
            <div>
              <h2 className="text-xl font-bold text-cyan-300">Sign In to SuperJarvis</h2>
              <p className="text-cyan-400/50 text-xs">Choose your sign-in method</p>
            </div>
          </div>

          {/* Initial State - Sign-In Options */}
          {step === 'idle' && !showEmailForm && (
            <div className="space-y-4">
              {/* Google Sign-In Button */}
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center space-x-3 py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 bg-white text-gray-700 hover:bg-gray-100 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <GoogleLogo className="w-5 h-5" />
                <span>Continue with Google</span>
              </button>

              {/* Divider */}
              <div className="flex items-center space-x-4 my-4">
                <div className="flex-1 h-px bg-cyan-400/20" />
                <span className="text-cyan-400/40 text-xs uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-cyan-400/20" />
              </div>

              {/* Email Magic Link Button */}
              <button
                onClick={() => setShowEmailForm(true)}
                disabled={loading}
                className="w-full py-3 rounded-xl font-bold tracking-wider uppercase text-sm transition-all duration-300 bg-cyan-400/10 text-cyan-400 border border-cyan-400/30 hover:bg-cyan-400/20 hover:border-cyan-400/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue with Email
              </button>

              {error && (
                <div className="text-red-400 text-sm text-center py-2 px-4 bg-red-400/10 border border-red-400/30 rounded-lg">
                  {error}
                </div>
              )}

              <p className="text-cyan-400/40 text-xs text-center">
                Secure, passwordless authentication
              </p>
            </div>
          )}

          {/* Email Form State */}
          {step === 'idle' && showEmailForm && (
            <form onSubmit={handleSendLink} className="space-y-5">
              <button
                type="button"
                onClick={handleBackToOptions}
                className="text-cyan-400/60 hover:text-cyan-400 text-sm transition-colors flex items-center space-x-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back to options</span>
              </button>

              <div className="space-y-2">
                <label className="text-xs font-bold text-cyan-400/70 uppercase tracking-wider">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-black/50 border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/30 focus:outline-none focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  disabled={loading}
                  autoFocus
                  required
                />
              </div>

              {error && (
                <div className="text-red-400 text-sm text-center py-2 px-4 bg-red-400/10 border border-red-400/30 rounded-lg">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className={`w-full py-3 rounded-xl font-bold tracking-wider uppercase text-sm transition-all duration-300 ${
                  loading || !email.trim()
                    ? 'bg-cyan-400/20 text-cyan-400/40 cursor-not-allowed'
                    : 'bg-cyan-400 text-black hover:bg-cyan-300 shadow-[0_0_25px_rgba(34,211,238,0.4)] hover:shadow-[0_0_40px_rgba(34,211,238,0.6)]'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    <span>Sending...</span>
                  </span>
                ) : (
                  'Send Magic Link'
                )}
              </button>

              <p className="text-cyan-400/40 text-xs text-center">
                We'll send you a secure link to sign in instantly
              </p>
            </form>
          )}

          {/* Google Pending State */}
          {step === 'google-pending' && (
            <div className="text-center space-y-5 py-6">
              <div className="w-14 h-14 mx-auto bg-white/10 rounded-full flex items-center justify-center">
                <GoogleLogo className="w-8 h-8" />
              </div>
              <div className="w-10 h-10 mx-auto border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
              <p className="text-cyan-400/70 text-sm tracking-wider">Connecting to Google...</p>
            </div>
          )}

          {/* Email Sent State */}
          {step === 'email-sent' && (
            <div className="text-center space-y-5">
              <div className="w-14 h-14 mx-auto bg-cyan-400/10 border border-cyan-400/30 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(34,211,238,0.2)]">
                <svg className="w-7 h-7 text-cyan-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <div>
                <h3 className="text-lg font-bold text-cyan-300 mb-1">Check Your Email</h3>
                <p className="text-cyan-400/60 text-sm">
                  Magic link sent to<br />
                  <span className="text-cyan-400 font-semibold">{sentToEmail}</span>
                </p>
              </div>

              <p className="text-cyan-400/40 text-xs">
                Click the link in your email to sign in. Expires in 1 hour.
              </p>

              <button
                onClick={handleBackToOptions}
                className="text-cyan-400/60 hover:text-cyan-400 text-sm transition-colors underline underline-offset-2"
              >
                Use a different method
              </button>
            </div>
          )}

          {/* Needs Email State - User opened link on different device */}
          {step === 'needs-email' && (
            <form onSubmit={handleCompleteSignIn} className="space-y-5">
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold text-cyan-300 mb-1">Confirm Your Email</h3>
                <p className="text-cyan-400/60 text-sm">
                  Enter the email you used to sign in
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-cyan-400/70 uppercase tracking-wider">
                  Email Address
                </label>
                <input
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-black/50 border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/30 focus:outline-none focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  disabled={loading}
                  autoFocus
                  required
                />
              </div>

              {error && (
                <div className="text-red-400 text-sm text-center py-2 px-4 bg-red-400/10 border border-red-400/30 rounded-lg">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !confirmEmail.trim()}
                className={`w-full py-3 rounded-xl font-bold tracking-wider uppercase text-sm transition-all duration-300 ${
                  loading || !confirmEmail.trim()
                    ? 'bg-cyan-400/20 text-cyan-400/40 cursor-not-allowed'
                    : 'bg-cyan-400 text-black hover:bg-cyan-300 shadow-[0_0_25px_rgba(34,211,238,0.4)] hover:shadow-[0_0_40px_rgba(34,211,238,0.6)]'
                }`}
              >
                {loading ? 'Signing In...' : 'Complete Sign In'}
              </button>
            </form>
          )}

          {/* Completing State */}
          {step === 'completing' && (
            <div className="text-center space-y-5 py-6">
              <div className="w-10 h-10 mx-auto border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
              <p className="text-cyan-400/70 text-sm tracking-wider">Signing you in...</p>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
